import { JobPlacementStatus } from '@company/types/job-placement.type';
import {
  defaultVariantColorsResolver,
  VariantColorsResolver,
} from '@mantine/core';

/**
 * Maps JobPlacementStatus to appropriate Mantine variant
 * Used for consistent status badge styling across the application
 */
export const getJobPlacementStatusVariant = (
  status: JobPlacementStatus
): string => {
  switch (status) {
    case JobPlacementStatus.OFFERED:
    case JobPlacementStatus.OFFER_UPDATED:
    case JobPlacementStatus.SHIFTS_UPDATED:
      return 'statusOpen';
    case JobPlacementStatus.ACCEPTED:
      return 'statusApproved';
    case JobPlacementStatus.CANCELLED:
    case JobPlacementStatus.DECLINED:
    case JobPlacementStatus.DECLINED_ADDITIONAL:
      return 'statusCancelled';
    case JobPlacementStatus.HIRED:
      return 'statusHired';
    default:
      return 'statusOpen';
  }
};

/**
 * Mantine variant color resolver for custom status badge variants
 * Handles the styling for statusOpen, statusApproved, statusCancelled, and statusHired variants
 */
export const variantColorResolver: VariantColorsResolver = (input) => {
  const defaultResolvedColors = defaultVariantColorsResolver(input);

  // Custom variants for status badges
  if (input.variant === 'statusOpen') {
    return {
      background: 'var(--white)',
      color: 'var(--black)',
      border: '1px solid var(--border-color)',
      hover: 'var(--white)',
    };
  }

  if (input.variant === 'statusApproved') {
    return {
      background: 'var(--light-green)',
      color: 'var(--green)',
      hover: 'var(--light-green)',
      border: 'none',
    };
  }

  if (input.variant === 'statusCancelled') {
    return {
      background: 'var(--light-red)',
      color: 'var(--red)',
      hover: 'var(--light-red)',
      border: 'none',
    };
  }

  if (input.variant === 'statusHired') {
    return {
      background: 'var(--yellow)',
      color: 'var(--black)',
      hover: 'var(--yellow)',
      border: 'none',
    };
  }

  if (input.variant === 'verified') {
    return {
      background: 'var(--light)',
      color: 'var(--green)',
      hover: 'var(--light)',
      border: 'none',
    };
  }

  if (input.variant === 'black') {
    return {
      background: 'var(--black)',
      color: 'var(--white)',
      hover: 'var(--black)',
      border: 'none',
    };
  }

  // Variants to match react-bootstrap badge color usage.
  if (input.variant === 'draft') {
    return {
      background: 'var(--secondary)',
      color: 'var(--black)',
      hover: 'var(--secondary)',
      border: 'none',
    };
  }

  if (input.variant === 'past') {
    return {
      background: 'var(--light-red)',
      color: 'var(--black)',
      hover: 'var(--light-red)',
      border: 'none',
    };
  }

  if (input.variant === 'tag') {
    return {
      background: 'var(--light)',
      color: 'var(--black)',
      hover: 'var(--light)',
      border: 'none',
    };
  }

  if (input.variant === 'insight') {
    return {
      background: 'var(--light-green)',
      color: 'var(--black)',
      hover: 'var(--light-green)',
      border: 'none',
    };
  }

  if (input.variant === 'count') {
    return {
      background: 'var(--yellow)',
      color: 'var(--black)',
      hover: 'var(--yellow)',
      border: 'none',
    };
  }

  // Candidate status tags used in the DetailV2 candidates list.
  if (input.variant === 'tagNew') {
    return {
      background: 'var(--green)',
      color: 'var(--light-green)',
      hover: 'var(--green)',
      border: 'none',
    };
  }

  if (input.variant === 'tagMatch') {
    return {
      background: 'var(--light-green)',
      color: 'var(--green)',
      hover: 'var(--light-green)',
      border: 'none',
    };
  }

  if (input.variant === 'tagApplied') {
    return {
      background: 'var(--lavender)',
      color: 'var(--blue)',
      hover: 'var(--lavender)',
      border: 'none',
    };
  }

  if (input.variant === 'tagSuggested') {
    return {
      background: '#DAFCD4',
      color: '#327E2C',
      hover: '#DAFCD4',
      border: 'none',
    };
  }

  return defaultResolvedColors;
};
